/**
 * How the hardware BACK key dismisses an open overlay: the app's modals close
 * when their own backdrop element is *tapped*, so the back handler has to
 * deliver a tap — a bare `Element.click()` is not one.
 *
 * Why this file exists (card t_9e54d28c, measured on the tablet): the app's
 * backdrops come in two shapes — 28 components dismiss on `@mousedown.self`
 * (`DeviceControlModal.vue:5`, the device sheet the owner opens daily, among
 * them) and 67 more on `@click.self`. A synthetic `.click()` fires only `click`,
 * so the `mousedown` half of those 28 never ran and the back key silently did
 * nothing while their sheet was open (measured: sheet open, `keyevent 4`, DOM
 * identical; the same element closed under a full pointer/mouse/click
 * sequence).
 *
 * The sequence below is what a finger produces on a backdrop, in that order, so
 * both patterns see their gesture. Dispatching on the backdrop element itself
 * (target === currentTarget) is what makes Vue's `.self` modifier match — the
 * sequence must never be dispatched on a child or an ancestor.
 */
export const BACKDROP_DISMISS_SEQUENCE = ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'] as const;

export type BackdropDismissEventType = (typeof BACKDROP_DISMISS_SEQUENCE)[number];

/** Anything with a DOM `dispatchEvent` — the dispatcher is testable with a double. */
export interface BackdropDismissTarget {
  dispatchEvent(event: Event): boolean;
}

export type BackdropEventFactory = (type: BackdropDismissEventType) => Event;

/**
 * The real events. `pointerType: 'mouse'` is deliberate: the app's edge-swipe
 * listener ignores mouse pointers outright, so a BACK dismissal can never be
 * read as a swipe gesture. `button: 0` / `isPrimary` make it an ordinary
 * left-button tap for anything that inspects the event.
 */
function makeBackdropEvent(type: BackdropDismissEventType): Event {
  const init = { bubbles: true, cancelable: true, composed: true, button: 0 };
  if (type.startsWith('pointer') && typeof PointerEvent === 'function') {
    return new PointerEvent(type, { ...init, pointerId: 1, pointerType: 'mouse', isPrimary: true });
  }
  return new MouseEvent(type, init);
}

/**
 * Tap `backdrop` the way a finger would and report the event types delivered,
 * so a caller (or a rig) can say what the overlay was actually given.
 *
 * One event failing must not cost the others: `click` is the last chance for a
 * `@click.self` overlay, and it comes after the `mousedown` a `@mousedown.self`
 * overlay needs.
 */
export function dismissBackdrop(
  backdrop: BackdropDismissTarget,
  makeEvent: BackdropEventFactory = makeBackdropEvent,
): BackdropDismissEventType[] {
  const delivered: BackdropDismissEventType[] = [];
  for (const type of BACKDROP_DISMISS_SEQUENCE) {
    try {
      backdrop.dispatchEvent(makeEvent(type));
      delivered.push(type);
    } catch {
      // Nothing to do with it here: the remaining events still go out.
    }
  }
  return delivered;
}
